// Mercapoli - Registro de Comercio Público
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Store, Loader2, CheckCircle, MapPin, Phone, Mail, Building2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import apiClient from '../lib/api';
import { toast } from 'sonner';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_mercadelivery-1/artifacts/5y038jeg_icono_mercapoli.png";

const RegistroComercio = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [tiposComercio, setTiposComercio] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    // Paso 1: Datos del comercio
    nombre_comercio: '',
    tipo_comercio_id: '',
    descripcion: '',
    direccion: '',
    ciudad: '',
    pais: 'GT',
    telefono_comercio: '',
    email_comercio: '',
    
    // Paso 2: Datos del propietario
    nombre_propietario: '',
    email_propietario: '',
    telefono_propietario: '',
    documento_identidad: '',
    
    // Paso 3: Datos fiscales
    nit_rfc: '',
    razon_social: '',
    
    // Términos
    acepta_terminos: false
  });

  useEffect(() => {
    loadTiposComercio();
  }, []);

  const loadTiposComercio = async () => {
    try {
      const response = await apiClient.get('/tipos-comercio');
      setTiposComercio(response.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateStep = (stepNum) => {
    if (stepNum === 1) {
      if (!formData.nombre_comercio || !formData.tipo_comercio_id || !formData.direccion || !formData.ciudad || !formData.telefono_comercio) {
        toast.error('Por favor completa todos los campos requeridos');
        return false;
      }
    } else if (stepNum === 2) {
      if (!formData.nombre_propietario || !formData.email_propietario || !formData.telefono_propietario || !formData.documento_identidad) {
        toast.error('Por favor completa todos los campos requeridos');
        return false;
      }
    } else if (stepNum === 3) {
      if (!formData.acepta_terminos) {
        toast.error('Debes aceptar los términos y condiciones');
        return false;
      }
    }
    return true;
  };

  const nextStep = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    }
  };

  const prevStep = () => {
    setStep(step - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) return;
    
    setLoading(true);
    try {
      await apiClient.post('/solicitudes/comercio', formData);
      setSubmitted(true);
      toast.success('¡Solicitud enviada exitosamente!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar solicitud');
    }
    setLoading(false);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <CardContent className="pt-8 pb-8">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold mb-2">¡Solicitud Recibida!</h2>
            <p className="text-muted-foreground mb-6">
              Hemos recibido tu solicitud para unirte a Mercapoli. 
              Nuestro equipo la revisará y te contactaremos en las próximas 24-48 horas.
            </p>
            <Link to="/">
              <Button className="bg-brand-primary hover:bg-brand-secondary">
                Volver al inicio
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="container-app">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Mercapoli" className="w-8 h-8" />
              <span className="font-heading font-bold">Mercapoli</span>
            </Link>
            <Link to="/">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Volver
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <div className="container-app py-8">
        <div className="max-w-2xl mx-auto">
          {/* Progress */}
          <div className="flex items-center justify-center gap-2 mb-8">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  s === step ? 'bg-brand-primary text-white' : 
                  s < step ? 'bg-green-500 text-white' : 'bg-muted text-muted-foreground'
                }`}>
                  {s < step ? <CheckCircle className="h-4 w-4" /> : s}
                </div>
                {s < 3 && (
                  <div className={`w-12 h-1 mx-1 ${s < step ? 'bg-green-500' : 'bg-muted'}`} />
                )}
              </div>
            ))}
          </div>

          <Card>
            <CardHeader className="text-center">
              <div className="w-14 h-14 rounded-xl bg-brand-primary/10 flex items-center justify-center mx-auto mb-4">
                <Store className="h-7 w-7 text-brand-primary" />
              </div>
              <CardTitle>Registrar mi Comercio</CardTitle>
              <CardDescription>
                {step === 1 && 'Cuéntanos sobre tu negocio'}
                {step === 2 && 'Datos del propietario'}
                {step === 3 && 'Información fiscal y términos'}
              </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-4">
              {/* Step 1: Datos del comercio */}
              {step === 1 && (
                <>
                  <div className="space-y-2">
                    <Label>Nombre del Comercio *</Label>
                    <Input
                      value={formData.nombre_comercio}
                      onChange={(e) => handleChange('nombre_comercio', e.target.value)}
                      placeholder="Ej: Farmacia La Salud"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Tipo de Comercio *</Label>
                    <Select value={formData.tipo_comercio_id} onValueChange={(v) => handleChange('tipo_comercio_id', v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecciona una categoría" />
                      </SelectTrigger>
                      <SelectContent>
                        {tiposComercio.map(tipo => (
                          <SelectItem key={tipo.tipo_id} value={tipo.tipo_id}>
                            {tipo.icono} {tipo.nombre}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Descripción</Label>
                    <Textarea
                      value={formData.descripcion}
                      onChange={(e) => handleChange('descripcion', e.target.value)}
                      placeholder="Describe brevemente tu comercio..."
                      rows={3}
                    />
                  </div>
                  
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Dirección *</Label>
                      <Input
                        value={formData.direccion}
                        onChange={(e) => handleChange('direccion', e.target.value)}
                        placeholder="Calle, número, zona"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Ciudad *</Label>
                      <Input
                        value={formData.ciudad}
                        onChange={(e) => handleChange('ciudad', e.target.value)}
                        placeholder="Ciudad de Guatemala"
                      />
                    </div>
                  </div>
                  
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Teléfono del Comercio *</Label>
                      <Input
                        value={formData.telefono_comercio}
                        onChange={(e) => handleChange('telefono_comercio', e.target.value)}
                        placeholder="+502 2222 3333"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email del Comercio</Label>
                      <Input
                        type="email"
                        value={formData.email_comercio}
                        onChange={(e) => handleChange('email_comercio', e.target.value)}
                        placeholder="contacto@micomercio.com"
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Step 2: Datos del propietario */}
              {step === 2 && (
                <>
                  <div className="space-y-2">
                    <Label>Nombre Completo del Propietario *</Label>
                    <Input
                      value={formData.nombre_propietario}
                      onChange={(e) => handleChange('nombre_propietario', e.target.value)}
                      placeholder="Juan Pérez López"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>DPI / Cédula *</Label>
                    <Input
                      value={formData.documento_identidad}
                      onChange={(e) => handleChange('documento_identidad', e.target.value)}
                      placeholder="1234 56789 0101"
                    />
                  </div>
                  
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Email Personal *</Label>
                      <Input
                        type="email"
                        value={formData.email_propietario}
                        onChange={(e) => handleChange('email_propietario', e.target.value)}
                        placeholder="tu@email.com"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Teléfono Personal *</Label>
                      <Input
                        value={formData.telefono_propietario}
                        onChange={(e) => handleChange('telefono_propietario', e.target.value)}
                        placeholder="+502 5555 6666"
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Step 3: Datos fiscales y términos */}
              {step === 3 && (
                <>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label>NIT (opcional)</Label>
                      <Input
                        value={formData.nit_rfc}
                        onChange={(e) => handleChange('nit_rfc', e.target.value)}
                        placeholder="1234567-8"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Razón Social (opcional)</Label>
                      <Input
                        value={formData.razon_social}
                        onChange={(e) => handleChange('razon_social', e.target.value)}
                        placeholder="Nombre legal del negocio"
                      />
                    </div>
                  </div>
                  
                  <div className="p-4 bg-muted/50 rounded-lg space-y-3">
                    <h4 className="font-medium">Resumen de tu solicitud</h4>
                    <div className="grid gap-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Comercio:</span>
                        <span>{formData.nombre_comercio}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Ubicación:</span>
                        <span>{formData.ciudad}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Propietario:</span>
                        <span>{formData.nombre_propietario}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="terms"
                      checked={formData.acepta_terminos}
                      onCheckedChange={(v) => handleChange('acepta_terminos', v)}
                    />
                    <label htmlFor="terms" className="text-sm text-muted-foreground">
                      Acepto los <a href="#" className="text-brand-primary underline">términos y condiciones</a> y 
                      la <a href="#" className="text-brand-primary underline">política de privacidad</a> de Mercapoli.
                    </label>
                  </div>
                </>
              )}

              {/* Navigation buttons */}
              <div className="flex justify-between pt-4">
                {step > 1 ? (
                  <Button variant="outline" onClick={prevStep}>
                    Anterior
                  </Button>
                ) : (
                  <div />
                )}
                
                {step < 3 ? (
                  <Button onClick={nextStep} className="bg-brand-primary hover:bg-brand-secondary">
                    Siguiente
                  </Button>
                ) : (
                  <Button 
                    onClick={handleSubmit} 
                    disabled={loading}
                    className="bg-brand-primary hover:bg-brand-secondary"
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Enviar Solicitud
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default RegistroComercio;
